import React, { useState, useEffect, useMemo } from "react";
import CreatableSelect from "react-select/creatable";
import "./GoogleCalendar.css"; 
import api from "../api";

const GoogleCalendarTest = ({ participantEmail, participantName, company }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [newEvent, setNewEvent] = useState({
    customTitle: "",
    customDescription: "",
    eventType: "Google Meet",
    location: "",
    startDateTime: "",
    endDateTime: "",
    guests: participantEmail ? [{ value: participantEmail, label: participantEmail }] : []
  });

  useEffect(() => {
    if (participantEmail) {
      setNewEvent((prev) => ({
        ...prev,
        guests: [{ value: participantEmail, label: participantEmail }]
      }));
    }
  }, [participantEmail]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/google/calendar/events");
      console.log("Fetched events:", response.data);
      setEvents(response.data);
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const relevantEvents = useMemo(() => {
    if (!participantName && !participantEmail && !company) return events;
    const terms = [];
    if (participantName) terms.push(participantName.toLowerCase());
    if (participantEmail) terms.push(participantEmail.toLowerCase());
    if (company) terms.push(company.toLowerCase());

    return events.filter((event) => {
      const title = event.summary ? event.summary.toLowerCase() : "";
      const description = event.description ? event.description.toLowerCase() : "";
      
      // Check attendees/participants list
      const attendees = event.attendees || [];
      const attendeeEmails = attendees.map(a => (a.email || "").toLowerCase());
      const attendeeEmailMatch = participantEmail && attendeeEmails.some(email => email.includes(participantEmail.toLowerCase()));
      
      // Check if any term appears in title, description, or if participant email is in attendees
      return attendeeEmailMatch || terms.some((term) => title.includes(term) || description.includes(term));
    });
  }, [events, participantName, participantEmail, company]);

  const filteredEvents = useMemo(() => {
    if (!searchQuery) return relevantEvents;
    const lowerQuery = searchQuery.toLowerCase();
    return relevantEvents.filter((event) => {
      const title = event.summary || "";
      const description = event.description || "";
      return (
        title.toLowerCase().includes(lowerQuery) ||
        description.toLowerCase().includes(lowerQuery)
      );
    });
  }, [relevantEvents, searchQuery]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuestsChange = (selectedOptions) => {
    setNewEvent((prev) => ({ ...prev, guests: selectedOptions || [] }));
  };

  useEffect(() => {
    if (newEvent.startDateTime) {
      const start = new Date(newEvent.startDateTime);
      let end = new Date(newEvent.endDateTime);
      if (!newEvent.endDateTime || end <= start) {
        end = new Date(start.getTime() + 60 * 60 * 1000);
        const pad = (num) => String(num).padStart(2, "0");
        const year = end.getFullYear();
        const month = pad(end.getMonth() + 1);
        const day = pad(end.getDate());
        const hours = pad(end.getHours());
        const minutes = pad(end.getMinutes());
        const formatted = `${year}-${month}-${day}T${hours}:${minutes}`;
        setNewEvent((prev) => ({ ...prev, endDateTime: formatted }));
      }
    }
  }, [newEvent.startDateTime]);

  const formatDateTime = (datetimeLocal) => {
    const date = new Date(datetimeLocal);
    return date.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const finalTitle = newEvent.customTitle
        ? `${newEvent.customTitle} with ${participantName}`
        : participantName || "Meeting";

      const finalDescription = newEvent.customDescription
        ? `${newEvent.customDescription} – ${company}`
        : company || "";

      const attendees = newEvent.guests.map((guest) => ({ email: guest.value }));

      const payload = {
        summary: finalTitle,
        description: finalDescription,
        startDateTime: formatDateTime(newEvent.startDateTime),
        endDateTime: formatDateTime(newEvent.endDateTime)
      };

      if (newEvent.eventType === "Physical Location" && newEvent.location) {
        payload.location = newEvent.location;
      }

      if (attendees.length > 0) {
        payload.attendees = attendees;
      }

      console.log("Creating event with payload:", payload);
      const response = await api.post("/google/calendar/event", payload);
      console.log("Event created:", response.data);
      setEvents((prev) => [...prev, response.data]);

      setNewEvent({
        customTitle: "",
        customDescription: "",
        eventType: "Google Meet",
        location: "",
        startDateTime: "",
        endDateTime: "",
        guests: participantEmail ? [{ value: participantEmail, label: participantEmail }] : []
      });
    } catch (err) {
      console.error("Error creating event:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="google-calendar-test-container">

      <h2>Relevant Calendar Events</h2>
      <div className="google-calendar-test-section" style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <label style={{ marginBottom: 4 }}>
          <span className="material-icons" style={{ verticalAlign: "middle" }}>search</span> Search events:
        </label>
        <input
          type="text"
          className="google-calendar-test-search-input"
          placeholder="Search relevant events..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <button
        className="google-calendar-test-button"
        style={{ marginBottom: 10 }}
        onClick={fetchEvents}
      >
        Refresh Events
      </button>
      {filteredEvents.length > 0 ? (
        <ul className="google-calendar-test-event-list">
          {filteredEvents.map((event) => (
            <li key={event.id} className="google-calendar-test-event-item">
              <strong>{event.summary}</strong>{" "}
              {event.start?.dateTime &&
                `- ${new Date(event.start.dateTime).toLocaleString()}`}
              {event.location && ` at ${event.location}`}
            </li>
          ))}
        </ul>
      ) : (
        <p>
          No relevant events have been scheduled
          {searchQuery ? ` for "${searchQuery}"` : ""}.
        </p>
      )}

      <h2 style={{ marginTop: 24 }}>Schedule New Event</h2>
      <div className="google-calendar-test-header">
        <span className="material-icons" style={{ marginRight: 8 }}>event</span>
        <input
          type="text"
          className="google-calendar-test-title-input"
          placeholder="Add title"
          value={newEvent.customTitle}
          onChange={(e) => setNewEvent({ ...newEvent, customTitle: e.target.value })}
        />
      </div>

      <div className="google-calendar-test-section">
        <label><span className="material-icons">schedule</span> Start:</label>
        <input
          type="datetime-local"
          className="google-calendar-test-input"
          name="startDateTime"
          value={newEvent.startDateTime}
          onChange={handleInputChange}
          required
        />
      </div>
      <div className="google-calendar-test-section">
        <label><span className="material-icons">schedule</span> End:</label>
        <input
          type="datetime-local"
          className="google-calendar-test-input"
          name="endDateTime"
          value={newEvent.endDateTime}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="google-calendar-test-section">
        <label><span className="material-icons">videocam</span> Event Type:</label>
        <select
          className="google-calendar-test-select"
          name="eventType"
          value={newEvent.eventType}
          onChange={handleInputChange}
        >
          <option value="Google Meet">Google Meet</option>
          <option value="Physical Location">Physical Location</option>
        </select>
      </div>
      {newEvent.eventType === "Physical Location" && (
        <div className="google-calendar-test-section">
          <label><span className="material-icons">location_on</span> Location:</label>
          <input
            type="text"
            className="google-calendar-test-input"
            name="location"
            value={newEvent.location}
            onChange={handleInputChange}
            placeholder="Enter physical location"
            required
          />
        </div>
      )}

      <div className="google-calendar-test-section">
        <label><span className="material-icons">people</span> Guests:</label>
        <div className="google-calendar-test-react-select-container">
          <CreatableSelect
            isMulti
            classNamePrefix="google-calendar-test-react-select"
            onChange={handleGuestsChange}
            value={newEvent.guests}
            placeholder="Type email and press Enter"
          />
        </div>
      </div>

      {company && (
        <div className="google-calendar-test-section">
          <label><span className="material-icons">business</span> Company:</label>
          <input
            type="text"
            className="google-calendar-test-input"
            value={company}
            disabled
          />
        </div>
      )}

      {participantName && (
        <div className="google-calendar-test-section">
          <label><span className="material-icons">person</span> Participant:</label>
          <input
            type="text"
            className="google-calendar-test-input"
            value={participantName}
            disabled
          />
        </div>
      )}

      <div className="google-calendar-test-section">
        <label><span className="material-icons">description</span> Description:</label>
        <textarea
          className="google-calendar-test-textarea"
          name="customDescription"
          value={newEvent.customDescription}
          onChange={handleInputChange}
          placeholder="Enter additional details"
        />
      </div>

      <div className="google-calendar-test-footer">
        <button
          type="button"
          className="google-calendar-test-button"
          onClick={handleSubmit}
        >
          Save
        </button>
      </div>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
};

export default GoogleCalendarTest;
